import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getFinancialYears,
  getFrameworks,
  getIndustries,
  getGhgDatabases,
  getKeywords,
  getModules,
  getUnitMaster,
} from "../kpiGridApi";
import { TAG_PATH_KEYS } from "./options";


const normalizeUnits = (units) => units.map((unit) => ({ ...unit, id: Number(unit.id) }));


export function useKpiCatalogues() {
  const [modules, setModules] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [unitMaster, setUnitMaster] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [ghgDatabases, setGhgDatabases] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [modRes, fyRes, unitRes, kwRes, dbRes, fwRes, indRes] = await Promise.all([
        getModules(),
        getFinancialYears(),
        getUnitMaster(),
        getKeywords(),
        getGhgDatabases(),
        getFrameworks(),
        getIndustries(),
      ]);
      setModules(modRes?.data?.data || []);
      setFinancialYears(fyRes?.data?.data || []);
      setUnitMaster(normalizeUnits(unitRes?.data?.data || unitRes?.data || []));
      setKeywords(kwRes?.data?.data || []);
      setGhgDatabases(dbRes?.data?.data || []);
      setFrameworks(fwRes?.data?.data || []);
      setIndustries(indRes?.data?.data || []);
    } catch (e) {
      setError(e?.message || "Could not load the KPI reference lists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);


  const tagValuesByKey = useMemo(() => {
    const index = {};
    keywords.forEach(({ keyword_type: key, keyword_value: value }) => {
      if (!index[key]) index[key] = [];
      if (!index[key].includes(value)) index[key].push(value);
    });
    Object.keys(index).forEach((key) => index[key].sort((a, b) => a.localeCompare(b)));
    return index;
  }, [keywords]);

  const addKeyword = useCallback((keywordType, keywordValue) => {
    setKeywords((current) =>
      current.some((k) => k.keyword_type === keywordType && k.keyword_value === keywordValue)
        ? current
        : [...current, { keyword_type: keywordType, keyword_value: keywordValue }],
    );
  }, []);

  const tagKeys = useMemo(() => {
    const fromKeywords = Object.keys(tagValuesByKey);
    return [...new Set([...TAG_PATH_KEYS, ...fromKeywords])];
  }, [tagValuesByKey]);

  return {
    modules,
    financialYears,
    unitMaster,
    ghgDatabases,
    frameworks,
    industries,
    tagKeys,
    tagValuesByKey,
    addKeyword,
    loading,
    error,
    reload: load,
  };
}
